/**
 * Connector Detail — Wave 2 finish.
 *
 * Three tabs:
 *   1. Overview  — config + last-sync stats + paginated docs
 *   2. Run history — paginated runs table with status + counters
 *   3. Settings — display name / schedule / sync_enabled / config edit, and
 *      the default classification of its synced documents (decision 35),
 *      drawn and sent only for the controls a framework shows (decision 28)
 *
 * Wired to /api/v1/library/connectors/* via useConnectors hooks.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  ArrowLeft, RefreshCw, Loader2, Save, Trash2, Pause, Play,
  AlertCircle, CheckCircle, ExternalLink, Webhook, Github,
  Cloud, Building2, KeyRound, Copy,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { useHasPermission } from '@/lib/use-has-permission';
import {
  useConnectorDetail,
  useConnectorRuns,
  useConnectorDocs,
  useSyncNow,
  useUpdateConnector,
  useDeleteConnector,
  useRotateSigningSecret,
  webhookUrlFor,
  type ConnectorKind,
  type SyncSchedule,
} from '@/hooks/useConnectors';
import { useQueryClient } from '@tanstack/react-query';
// Decision 35 — the source's default classification, under the one
// classification-controls rule (decision 28).
import {
  invalidateClassificationPolicy,
  useClassificationPolicy,
  type ClassificationControlsView,
} from '@/lib/use-classification-policy';
import { ExportControlSelect, SensitivitySelect } from '@/components/library/classification-selects';
// L6b-08 — the system's pause, worded once for the card and this page.
import { SigningSecretDialog, copyWithReport, dropSecretOnClose } from '@/components/library/signing-secret-dialog';
import { SourcePauseBadge, SourcePauseNotice, useConnectSource } from '@/components/library/source-pause-notice';

export const Route = createFileRoute('/library/sources/$sourceId')({
  component: ConnectorDetailPage,
});

const KIND_ICONS: Record<ConnectorKind, LucideIcon> = {
  onedrive: Cloud,
  sharepoint: Building2,
  github: Github,
  webhook: Webhook,
};

const SCHEDULES: { value: SyncSchedule; label: string }[] = [
  { value: 'every_5min', label: 'Every 5 minutes' },
  { value: 'every_15min', label: 'Every 15 minutes' },
  { value: 'every_1h', label: 'Every hour' },
  { value: 'every_6h', label: 'Every 6 hours' },
  { value: 'daily', label: 'Daily' },
  { value: 'manual_only', label: 'Manual only' },
  { value: 'webhook_only', label: 'Webhook only' },
];

function ConnectorDetailPage() {
  const { sourceId } = Route.useParams();
  return <ConnectorDetail sourceId={sourceId} />;
}

/**
 * One connector. Library LB6-T3 / L6-04 — each control asks EXACTLY the key
 * its route declares (connectors.ts, LIBRARY_CONTROLS in @argusai/shared);
 * this page imported no permission hook at all before, so everyone saw every
 * control:
 *   the page (GET /library/connectors/:id, /runs, /docs) → read:library.documents
 *   Sync now (POST …/sync-now)                         → write:library
 *   the Settings tab (PATCH …/:id)                     → write:library
 *   Rotate secret (POST …/:id/rotate-secret, webhook)  → write:library
 *   Delete (DELETE …/:id)                              → delete:library
 * Exported for the per-role control test.
 */
export function ConnectorDetail({ sourceId }: { sourceId: string }) {
  const navigate = useNavigate();
  const canViewSource = useHasPermission('read', 'library.documents');
  const canEditSource = useHasPermission('write', 'library');
  const canDeleteSource = useHasPermission('delete', 'library');
  // Read only by a person who holds the page's key (no id → no call): without
  // it the page says Access Required and asks the server nothing.
  const { data: connector, isLoading } = useConnectorDetail(canViewSource ? sourceId : null);
  const syncNow = useSyncNow();
  const updateMutation = useUpdateConnector();
  const deleteMutation = useDeleteConnector();
  // #10 — styled delete confirmation instead of a native browser confirm.
  const [confirmDelete, setConfirmDelete] = useState(false);
  // L6b-08 — Reconnect for a source whose sign-in failed (called before the
  // early returns below: a hook is never conditional).
  const connectSource = useConnectSource(connector ? connectorKindLabel(connector.connectorKind) : 'Source');

  if (!canViewSource) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <AlertCircle className="h-12 w-12 text-muted-foreground/20 mb-3" />
        <h2 className="text-lg font-semibold">Access Required</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Your role does not include viewing external document sources.
        </p>
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!connector) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <AlertCircle className="h-12 w-12 text-muted-foreground/20 mb-3" />
        <h2 className="text-lg font-semibold">Connector not found</h2>
        <Button variant="outline" className="mt-4" onClick={() => navigate({ to: '/library/sources' })}>
          Back to Sources
        </Button>
      </div>
    );
  }

  const Icon = KIND_ICONS[connector.connectorKind];

  const requiresAuth = connector.connectorKind === 'onedrive' || connector.connectorKind === 'sharepoint' || connector.connectorKind === 'github';
  const isAuthMissing = requiresAuth && !connector.hasCredentials;
  const kindLabel = connectorKindLabel(connector.connectorKind);
  // L6b-08 / OQ-L6b-2 — sign-in failed: Sync now is refused (409) until the
  // source is reconnected, so the header offers Reconnect in its place.
  const needsReconnect = connector.pausedReason === 'credentials';

  const handleSync = async () => {
    if (isAuthMissing) {
      toast.error(`${kindLabel} not authenticated — connect first.`);
      return;
    }
    try {
      // Issue #90 — split success/error so an `error` status no longer
      // renders inside a green success toast as `+0 new`.
      const result = await syncNow.mutateAsync(connector.id);
      if (result.status === 'error') {
        toast.error(`Sync failed for ${connector.displayName}. Check Run history below.`);
      } else {
        toast.success(`Sync ${result.status} — +${result.docsAdded} new, ${result.docsSkippedDedup} dedup-skipped.`);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Sync failed');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(connector.id);
      toast.success('Connector deleted');
      setConfirmDelete(false);
      navigate({ to: '/library/sources' });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border/40 bg-background px-6 py-3 flex items-center gap-3 shrink-0">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          title="Back to Sources"
          onClick={() => navigate({ to: '/library/sources' })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Separator orientation="vertical" className="h-5" />
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon className="h-4 w-4 text-primary" />
        </div>
        <div className="flex-1">
          <h1 className="text-lg font-semibold">{connector.displayName}</h1>
          <p className="text-xs text-muted-foreground capitalize">
            {connector.connectorKind} • {connector.syncSchedule.replace(/_/g, ' ')}
          </p>
        </div>
        <SourcePauseBadge connector={connector} />
        {canEditSource && needsReconnect && (
        <Button
          variant="default"
          size="sm"
          onClick={() => connectSource.connect(connector.id)}
          disabled={connectSource.isPending}
          className="gap-1.5"
          title={`Sign in to ${kindLabel} again — this source's sign-in failed and its syncs are paused until it is reconnected.`}
        >
          {connectSource.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <KeyRound className="h-3 w-3" />}
          Reconnect
        </Button>
        )}
        {canEditSource && !needsReconnect && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleSync}
          disabled={syncNow.isPending || isAuthMissing}
          className="gap-1.5"
          title={isAuthMissing ? `Connect ${kindLabel} first — no credentials stored.` : 'Trigger an immediate sync run'}
        >
          {syncNow.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3" />
          )}
          {isAuthMissing ? `Connect ${kindLabel} first` : 'Sync now'}
        </Button>
        )}
        {canDeleteSource && (
        <Button
          variant="ghost"
          size="sm"
          className="text-red-500 hover:text-red-600 gap-1.5"
          onClick={() => setConfirmDelete(true)}
        >
          <Trash2 className="h-3 w-3" />
          Delete
        </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex-1 overflow-auto">
        <Tabs defaultValue="overview" className="p-6 max-w-5xl mx-auto">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="runs">Run history</TabsTrigger>
            {canEditSource && <TabsTrigger value="settings">Settings</TabsTrigger>}
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="space-y-4 mt-4">
            <SourcePauseNotice connector={connector} />
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Last sync</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <Stat label="Status" value={connector.lastSyncStatus ?? 'never'} />
                <Stat label="Last run" value={connector.lastSyncAt ?? 'never'} />
                <Stat label="Docs synced" value={String(connector.lastSyncDocCount)} />
                <Stat
                  label="Duration"
                  value={connector.lastSyncDurationMs ? `${(connector.lastSyncDurationMs / 1000).toFixed(2)}s` : 'n/a'}
                />
              </CardContent>
            </Card>
            {connector.lastSyncError && (
              <Card className="border-red-500/30 bg-red-500/5">
                <CardContent className="pt-4 pb-3">
                  <p className="text-xs font-medium text-red-500 mb-1">Last error</p>
                  <p className="text-xs text-red-500 font-mono">{connector.lastSyncError}</p>
                </CardContent>
              </Card>
            )}
            <DocsTable connectorId={connector.id} />
          </TabsContent>

          {/* RUN HISTORY */}
          <TabsContent value="runs" className="mt-4">
            <RunsTable connectorId={connector.id} />
          </TabsContent>

          {/* SETTINGS — PATCH /library/connectors/:id (write:library) */}
          {canEditSource && (
            <TabsContent value="settings" className="mt-4">
              <SettingsForm
                connector={connector}
                onSave={async (input) => {
                  try {
                    await updateMutation.mutateAsync({ id: connector.id, ...input });
                    toast.success('Settings saved');
                  } catch (e) {
                    toast.error(e instanceof Error ? e.message : 'Save failed');
                  }
                }}
              />
              {/* Decision 62 — webhook sources only; POST …/rotate-secret (write:library) */}
              <SigningSecretCard connector={connector} />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* #10 — styled delete confirmation (replaces native window.confirm). */}
      <AlertDialog
        open={confirmDelete}
        onOpenChange={(open) => { if (!open && !deleteMutation.isPending) setConfirmDelete(false); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete connector &ldquo;{connector.displayName}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              Existing synced documents will remain in the library. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-connector-delete"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
              onClick={(e) => { e.preventDefault(); void handleDelete(); }}
            >
              {deleteMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : null}
              Delete connector
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function connectorKindLabel(kind: ConnectorKind): string {
  return kind === 'onedrive' ? 'OneDrive' : kind === 'sharepoint' ? 'SharePoint' : kind === 'github' ? 'GitHub' : kind;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] text-muted-foreground">{label}</p>
      <p className="font-medium capitalize truncate" title={value}>{value}</p>
    </div>
  );
}

function DocsTable({ connectorId }: { connectorId: string }) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useConnectorDocs(connectorId, page, 25);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Synced documents</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mx-auto" />
          </div>
        ) : (data?.data ?? []).length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-6">
            No documents synced yet
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Source path</TableHead>
                <TableHead>Hash</TableHead>
                <TableHead>Last seen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.data.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell className="font-mono text-xs truncate max-w-md" title={doc.externalSourcePath}>
                    {doc.externalSourcePath}
                    {doc.removed && (
                      <p
                        className="font-sans text-[11px] text-muted-foreground"
                        title="A person deleted this document from the Library. Syncs do not bring it back while the source keeps it."
                      >
                        Removed from the Library{doc.removed.byName ? ` by ${doc.removed.byName}` : ''} on{' '}
                        {new Date(doc.removed.at).toLocaleDateString()}, not synced again
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{doc.contentHash.slice(0, 8)}</TableCell>
                  <TableCell className="text-xs">{new Date(doc.lastSeenAt).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {data && data.total > 25 && (
          <div className="flex items-center justify-between mt-3 text-xs">
            <span className="text-muted-foreground">
              Page {page} of {Math.ceil(data.total / 25)} • {data.total} total
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                Prev
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setPage((p) => p + 1)} disabled={page * 25 >= data.total}>
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RunsTable({ connectorId }: { connectorId: string }) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useConnectorRuns(connectorId, page, 25);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Run history</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin mx-auto" />
        ) : (data?.data ?? []).length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-6">No runs yet</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Started</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Added</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead>Skipped</TableHead>
                <TableHead>Deleted</TableHead>
                <TableHead>Duration</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.data.map((run) => (
                <TableRow key={run.id}>
                  <TableCell className="text-xs">{new Date(run.startedAt).toLocaleString()}</TableCell>
                  <TableCell className="text-xs capitalize">{run.runType}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        run.status === 'success' ? 'default'
                          : run.status === 'partial' ? 'outline'
                            : run.status === 'error' ? 'destructive'
                              : 'secondary'
                      }
                    >
                      {run.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">{run.docsAdded}</TableCell>
                  <TableCell className="text-xs">{run.docsUpdated}</TableCell>
                  <TableCell className="text-xs">{run.docsSkippedDedup}</TableCell>
                  <TableCell className="text-xs">{run.docsDeleted}</TableCell>
                  <TableCell className="text-xs">{run.durationMs ? `${(run.durationMs / 1000).toFixed(2)}s` : '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {data && data.total > 25 && (
          <div className="flex items-center justify-between mt-3 text-xs">
            <span className="text-muted-foreground">
              Page {page} of {Math.ceil(data.total / 25)} • {data.total} total
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                Prev
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setPage((p) => p + 1)} disabled={page * 25 >= data.total}>
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface SettingsFormProps {
  connector: ReturnType<typeof useConnectorDetail>['data'] extends infer T
    ? T extends null | undefined
      ? never
      : NonNullable<T>
    : never;
  onSave: (input: SettingsPatch) => Promise<void>;
}

/** What the Settings tab sends (PATCH /library/connectors/:id). */
export interface SettingsPatch {
  displayName?: string;
  syncSchedule?: SyncSchedule;
  syncEnabled?: boolean;
  defaultSensitivity?: string;
  defaultExportControl?: string;
}

/**
 * The Settings tab's PATCH body. A default classification goes in ONLY for a
 * control the tenant's frameworks show (decision 28) AND only when it
 * changed — an unchanged default is not re-asserted, so a person below a
 * stored default's clearance can still rename the source. A hidden control
 * sends no key. Exported for the test.
 */
export function settingsPatch(
  values: Required<Pick<SettingsPatch, 'displayName' | 'syncSchedule' | 'syncEnabled'>> & {
    defaultSensitivity: string;
    defaultExportControl: string;
  },
  stored: { defaultSensitivity: string; defaultExportControl: string },
  controls: ClassificationControlsView,
): SettingsPatch {
  return {
    displayName: values.displayName,
    syncSchedule: values.syncSchedule,
    syncEnabled: values.syncEnabled,
    ...(controls.sensitivity && values.defaultSensitivity !== stored.defaultSensitivity
      && { defaultSensitivity: values.defaultSensitivity }),
    ...(controls.exportControl && values.defaultExportControl !== stored.defaultExportControl
      && { defaultExportControl: values.defaultExportControl }),
  };
}

export function SettingsForm({ connector, onSave }: SettingsFormProps) {
  const [displayName, setDisplayName] = useState(connector.displayName);
  const [syncSchedule, setSyncSchedule] = useState<SyncSchedule>(connector.syncSchedule);
  const [syncEnabled, setSyncEnabled] = useState(connector.syncEnabled);
  // Decision 35 — the default classification of the source's synced documents.
  const [defaultSensitivity, setDefaultSensitivity] = useState(connector.defaultSensitivity);
  const [defaultExportControl, setDefaultExportControl] = useState(connector.defaultExportControl);
  const [saving, setSaving] = useState(false);
  // Hidden (no framework that shows them): neither drawn nor sent. An edit
  // need not wait for the answer — a key it does not send keeps the stored
  // default as it is.
  const { controls } = useClassificationPolicy();
  const queryClient = useQueryClient();

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(settingsPatch(
        { displayName, syncSchedule, syncEnabled, defaultSensitivity, defaultExportControl },
        connector,
        controls,
      ));
    } finally {
      // A framework turned on or off since the form opened: re-ask (review
      // LB6T7-BF-06).
      void invalidateClassificationPolicy(queryClient);
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 max-w-md">
        <div className="space-y-1.5">
          <Label className="text-xs">Display name</Label>
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="h-8 text-xs" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Sync schedule</Label>
          <Select value={syncSchedule} onValueChange={(v) => setSyncSchedule(v as SyncSchedule)}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCHEDULES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {controls.any ? (
          <div className="rounded-md border border-muted p-3 space-y-3" data-testid="source-default-classification">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Classification of synced documents</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Applies to documents this source writes from now on.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {controls.sensitivity ? <SensitivitySelect value={defaultSensitivity} onChange={setDefaultSensitivity} /> : null}
              {controls.exportControl ? <ExportControlSelect value={defaultExportControl} onChange={setDefaultExportControl} /> : null}
            </div>
          </div>
        ) : null}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium">Sync enabled</p>
            <p className="text-[10px] text-muted-foreground">When off, scheduled syncs are skipped.</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setSyncEnabled((v) => !v)}>
            {syncEnabled ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
            <span className="ml-1.5">{syncEnabled ? 'On' : 'Off'}</span>
          </Button>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-1.5">
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
          Save changes
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * Decision 62 — a webhook source's signing secret: shown once when the source
 * was created; Rotate issues a new one (POST …/:id/rotate-secret →
 * write:library, the key of the Settings tab this card sits in — LIBRARY_CONTROLS
 * `rotateSigningSecret`). Rendered ONLY inside the `canEditSource` Settings tab
 * and only for a webhook source; any other kind renders nothing. The new
 * secret lives in this card's state while the show-once dialog is open, and is
 * dropped when it closes. Exported for the per-role control test (a Radix tab
 * that is not the default renders nothing server-side).
 */
export function SigningSecretCard({ connector }: { connector: { id: string; connectorKind: ConnectorKind } }) {
  const rotate = useRotateSigningSecret();
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [rotatedSecret, setRotatedSecret] = useState<string | null>(null);
  if (connector.connectorKind !== 'webhook') return null;
  const webhookUrl = webhookUrlFor(connector.id);

  const handleRotate = async () => {
    try {
      const rotated = await rotate.mutateAsync(connector.id);
      setRotatedSecret(rotated.signingSecret);
      setConfirmRotate(false);
    } catch (e) {
      setConfirmRotate(false);
      toast.error(e instanceof Error ? e.message : 'Rotate failed');
    }
  };

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-sm">Signing secret</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 max-w-md">
        <div className="space-y-1.5">
          <Label className="text-xs">Webhook URL</Label>
          <div className="flex gap-2">
            <Input value={webhookUrl} readOnly className="h-8 text-xs font-mono" aria-label="Webhook URL" />
            <Button
              variant="ghost"
              size="sm"
              title="Copy the webhook URL"
              onClick={() => void copyWithReport(webhookUrl, 'url')}
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          The secret was shown once when this source was created. Rotating issues a new one and stops the current one
          immediately.
        </p>
        <Button
          size="sm"
          variant="outline"
          title="Issue a new signing secret — the current one stops working at once"
          onClick={() => setConfirmRotate(true)}
        >
          <KeyRound className="h-3.5 w-3.5 mr-1.5" />
          Rotate secret
        </Button>
      </CardContent>

      <AlertDialog
        open={confirmRotate}
        onOpenChange={(open) => { if (!open && !rotate.isPending) setConfirmRotate(false); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rotate the signing secret?</AlertDialogTitle>
            <AlertDialogDescription>
              A new secret is issued and shown once. The current secret stops working immediately; deliveries signed
              with it are refused until the sender is updated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={rotate.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-rotate-secret"
              disabled={rotate.isPending}
              onClick={(e) => { e.preventDefault(); void handleRotate(); }}
            >
              {rotate.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : null}
              Rotate secret
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {rotatedSecret !== null && (
        <SigningSecretDialog
          open
          onOpenChange={dropSecretOnClose(() => setRotatedSecret(null), rotate.reset)}
          secret={rotatedSecret}
          webhookUrl={webhookUrl}
          context="rotated"
        />
      )}
    </Card>
  );
}
